import {
  StackLayout,
  Button,
  HeadingText,
  ParagraphText,
  Image,
} from '@leagueplatform/genesis-core';
import { FullScreenContainer } from 'components/full-screen-container/full-screen-container.component';
import { ErrorTypes, useErrorMessage } from 'hooks/use-error-message.hook';
import { useIntl } from '@leagueplatform/locales';
import { useNavigate } from 'react-router-dom';

import { APP_MANAGED_ROUTES } from 'common/constants';
import {
  sendErrorScreenLoadedEvent,
  sendErrorButtonClickedEvent,
} from 'utils/analytics-events';
import errorIcon from 'assets/error-2.svg';

export const FullPageError = ({
  errorType,
  customMessage,
}: {
  errorType: ErrorTypes;
  customMessage?: string;
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { errorHeading, errorCaption } = useErrorMessage(
    errorType,
    customMessage,
  );

  // Map error types to analytics screen names
  const getScreenName = (type: ErrorTypes): string => {
    switch (type) {
      case ErrorTypes.NOT_ELIGIBLE:
        return 'eligibility error';
      case ErrorTypes.NO_CONNECTION:
        return 'sign out error';
      case ErrorTypes.API_CONNECTION_ERROR:
      case ErrorTypes.API_ERROR:
      default:
        return 'generic error';
    }
  };

  const screenName = getScreenName(errorType);

  // Track screen loaded event (Items 108, 110, 112)
  sendErrorScreenLoadedEvent(screenName);

  // Handle button click analytics with navigation
  const handleButtonClickWithNavigation = (detail: string, route: string) => {
    sendErrorButtonClickedEvent(screenName, detail);
    navigate(route);
  };

  const handleButtonClickWithReload = (detail: string) => {
    sendErrorButtonClickedEvent(screenName, detail);
    window.location.reload();
  };

  return (
    <FullScreenContainer>
      <StackLayout
        orientation="vertical"
        horizontalAlignment="center"
        verticalAlignment="center"
        css={{
          height: '100%',
          whiteSpace: 'pre-wrap',
        }}
      >
        <Image
          src={errorIcon}
          alt="error icon"
          width="147px"
          height="auto"
          css={{
            position: 'relative',
            top: '-$one',
            marginBottom: '$three',
          }}
        />
        <HeadingText
          level="1"
          size="xl"
          css={{
            marginBottom: '$half',
          }}
        >
          {errorHeading}
        </HeadingText>
        <ParagraphText
          emphasis="subtle"
          css={{ textAlign: 'center', maxWidth: '351px' }}
        >
          {errorCaption}
        </ParagraphText>

        {/* Primary action varies by error type */}
        {(() => {
          const commonCss = {
            marginTop: '$two',
            minWidth: '200px',
            height: '48px',
            justifyContent: 'center',
          } as const;

          switch (errorType) {
            case ErrorTypes.NOT_ELIGIBLE:
              return (
                <Button
                  css={commonCss}
                  onClick={() =>
                    handleButtonClickWithNavigation(
                      'go back to log in',
                      APP_MANAGED_ROUTES.SIGN_OUT,
                    )
                  }
                >
                  {formatMessage({ id: 'STR_GO_BACK_TO_LOG_IN' })}
                </Button>
              );
            case ErrorTypes.NO_CONNECTION:
              return (
                <Button
                  css={commonCss}
                  onClick={() =>
                    handleButtonClickWithNavigation(
                      'sign out',
                      APP_MANAGED_ROUTES.SIGN_OUT,
                    )
                  }
                >
                  {formatMessage({ id: 'STR_LOG_OUT' })}
                </Button>
              );
            case ErrorTypes.API_CONNECTION_ERROR:
            case ErrorTypes.API_ERROR:
            default:
              return (
                <Button
                  css={commonCss}
                  onClick={() => handleButtonClickWithReload('reload')}
                >
                  {formatMessage({ id: 'STR_RELOAD' })}
                </Button>
              );
          }
        })()}
      </StackLayout>
    </FullScreenContainer>
  );
};
